import React, { createContext, useEffect, useState, useCallback, useRef } from "react";
import type {
  AuthContextType,
  User,
  Customer,
  Organization,
  LoginRequest,
  RegisterRequest,
  MagicLinkRequest,
} from "../types/auth";
import { apiService } from "../services/api";
import { pushNotificationService } from "../services/pushNotificationService";
import { getApiErrorMessage } from "../lib/apiErrors";
import { clearCustomerDataForLogout, clearOrganizationCache } from "../lib/portal-loaders";
// helpdesk-utils no longer needed after unified multi-org view
import { isTokenExpired, isTokenNearExpiry } from "../lib/tokenUtils";
import { refreshTokenManager } from "../lib/refreshTokenManager";
import { queryClient } from "../lib/queryClient";

// Type definition for portal context stored on window
interface PortalContext {
  organizationInfo?: {
    organizationId: string;
    organizationSlug: string;
    organizationName: string;
  };
}

// Extend window interface to include portal context
declare global {
  interface Window {
    __PORTAL_CONTEXT__?: PortalContext;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to determine if an API error should trigger logout
const shouldLogoutOnError = (statusCode?: number): boolean => {
  // Only logout on authentication/authorization errors
  return statusCode === 401 || statusCode === 403;
};

// Helper function to get error type for logging
const getErrorType = (statusCode?: number): string => {
  if (statusCode === 429) return 'Rate Limiting';
  if (statusCode && statusCode >= 500) return 'Server Error';
  if (statusCode && statusCode >= 400) return 'Client Error';
  return 'Network/Unknown Error';
};

// Helper function to decode JWT and extract organizationId
const getOrganizationIdFromToken = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.organization_id || null;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<
    Organization[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPortalContextReady, setIsPortalContextReady] = useState(() => {
    // Initialize based on current route and portal context availability
    const isPortalRoute = window.location.pathname.includes('/portal');
    if (!isPortalRoute) {
      return true; // Not a portal route, so context is not needed
    }
    // For portal routes, check if context is already available
    const portalContext = window.__PORTAL_CONTEXT__;
    return !!portalContext?.organizationInfo?.organizationId;
  });

  // Mutex to prevent concurrent refresh attempts
  const isRefreshingRef = useRef(false);

  const updateOrganizationData = useCallback((userData: User) => {
    // Extract available organizations from user data
    const organizations =
      userData.userOrganizations?.map((uo) => uo.organization) || [];
    setAvailableOrganizations(organizations);

    // Set current organization (primary organization or first available)
    const primaryOrg =
      organizations.find((org) => org.id === userData.organizationId) ||
      organizations[0];
    setCurrentOrganization(primaryOrg || null);
  }, []);

  // Clear all helpdesk filter preferences from localStorage
  const clearHelpdeskPreferences = () => {
    const keysToRemove = Object.keys(localStorage).filter(key =>
      key.startsWith('helpdesk.')
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  const clearAdminAuth = useCallback(() => {
    setUser(null);
    // Get organization-specific customer token if available
    const portalContext = window.__PORTAL_CONTEXT__;
    const orgId = portalContext?.organizationInfo?.organizationId;
    const customerToken = orgId
      ? localStorage.getItem(`customerToken_${orgId}`)
      : null;
    setToken(customer ? customerToken : null);
    setCurrentOrganization(null);
    setAvailableOrganizations([]);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("adminTheme");
    clearHelpdeskPreferences();
    clearOrganizationCache();
  }, [customer]);

  const clearCustomerAuth = useCallback(() => {
    setCustomer(null);
    setToken(user ? localStorage.getItem("authToken") : null);
    
    // Clear organization-specific customer tokens and themes
    const portalContext = window.__PORTAL_CONTEXT__;
    if (portalContext?.organizationInfo?.organizationId) {
      const orgId = portalContext.organizationInfo.organizationId;
      localStorage.removeItem(`customerToken_${orgId}`);
      localStorage.removeItem(`customerTheme_${orgId}`);
    } else {
      // Fallback: clear all customer tokens and themes
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('customerToken_') || key.startsWith('customerTheme_')) {
          localStorage.removeItem(key);
        }
      });
    }
    
    // Clear any legacy customer data
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerTheme');
  }, [user]);

  const clearAuth = useCallback(() => {
    setUser(null);
    setCustomer(null);
    setToken(null);
    setCurrentOrganization(null);
    setAvailableOrganizations([]);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("adminTheme");
    clearHelpdeskPreferences();

    // Clear all customer data (tokens and themes) using helper function
    clearCustomerDataForLogout();
  }, []);

  // Set up refresh function for the global manager
  const refreshTokenImplementation = useCallback(async (refreshToken: string) => {
    try {
      isRefreshingRef.current = true;

      const response = await apiService.refreshToken(refreshToken);

      if (response.success && response.data) {
        const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;

        console.log("Refresh token response successful, updating auth state...");
        // Update ALL storage and state
        localStorage.setItem("authToken", newToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        setToken(newToken);
        setUser(userData);
        updateOrganizationData(userData);
        console.log("Auth state updated after refresh");

        return {
          success: true,
          token: newToken,
          refreshToken: newRefreshToken,
          user: userData
        };
      }

      // Don't call clearAuth() here — the refresh token may still be valid
      // (e.g., transient network error, server error, rate limiting).
      // Only the 401 interceptor in api.ts should clear auth after definitive rejection.
      console.warn("Token refresh returned unsuccessful response");
      return { success: false };
    } catch (error) {
      // Don't call clearAuth() on network/transient errors — tokens may still be valid.
      // This is critical for mobile PWA where network may be temporarily unavailable
      // when the app resumes from background.
      console.error("Token refresh implementation failed:", error);
      return { success: false };
    } finally {
      isRefreshingRef.current = false;
    }
  }, [updateOrganizationData, clearAuth]);

  // Initialize the refresh function in the manager
  useEffect(() => {
    refreshTokenManager.setRefreshFunction(refreshTokenImplementation);
  }, [refreshTokenImplementation]);

  // Listen for session expired event from API interceptor
  // This ensures auth state is cleared before redirect to prevent loops
  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('Session expired event received, clearing auth state');
      // Stop proactive refresh timer
      refreshTokenManager.stopProactiveRefresh();
      setUser(null);
      setToken(null);
      setCustomer(null);
      setCurrentOrganization(null);
      setAvailableOrganizations([]);
      setIsLoading(false);
      // Clear TanStack Query cache to prevent stale data from persisting
      queryClient.clear();
      // Clear helpdesk filter preferences
      clearHelpdeskPreferences();
      // Clear organization cache
      clearOrganizationCache();
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Force refresh token using the global manager
  const forceRefreshToken = useCallback(async (): Promise<boolean> => {
    const result = await refreshTokenManager.refreshToken();
    return result.success;
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem("authToken");
      const storedRefreshToken = localStorage.getItem("refreshToken");

      // Check if we're in customer portal context
      const isCustomerPortalRoute = window.location.pathname.includes('/portal');

      if (isCustomerPortalRoute) {
        // For portal routes, we need to wait for the portal context to be available
        const portalContext = window.__PORTAL_CONTEXT__;

        if (!portalContext?.organizationInfo?.organizationId) {
          // Portal context not yet available, wait for it
          // This can happen during the initial page load before the route loader completes
          console.log("Portal context not available yet, will retry when available");

          // Mark that portal context is not ready
          setIsPortalContextReady(false);

          // Set up a polling mechanism to check for portal context
          const checkPortalContext = () => {
            const updatedPortalContext = window.__PORTAL_CONTEXT__;
            if (updatedPortalContext?.organizationInfo?.organizationId) {
              console.log("Portal context now available via polling, initializing auth");
              setIsPortalContextReady(true);
              initializeAuth();
            } else {
              // Continue polling every 50ms until context is available
              setTimeout(checkPortalContext, 50);
            }
          };

          // Start polling after a short delay
          setTimeout(checkPortalContext, 50);
          return; // Don't set isLoading to false yet
        }

        // Portal context is available
        setIsPortalContextReady(true);

        // Skip token validation if on auto-login page
        // The auto-login component will handle authentication
        const isAutoLoginPage = window.location.pathname.includes('/portal/auto-login');
        if (isAutoLoginPage) {
          console.log('On auto-login page, skipping token validation');
          setIsLoading(false);
          return;
        }

        // Get organization-specific customer token
        let customerToken: string | null = null;

        customerToken = localStorage.getItem(`customerToken_${portalContext.organizationInfo.organizationId}`);

        // Fallback: try legacy customerToken
        if (!customerToken) {
          customerToken = localStorage.getItem("customerToken");

          // If we have a legacy token, validate it matches the current organization
          if (customerToken) {
            const tokenOrgId = getOrganizationIdFromToken(customerToken);
            if (tokenOrgId !== portalContext.organizationInfo.organizationId) {
              // Token is for wrong organization, clear it
              localStorage.removeItem("customerToken");
              customerToken = null;
            } else {
              // Migrate legacy token to organization-specific key
              localStorage.setItem(`customerToken_${portalContext.organizationInfo.organizationId}`, customerToken);
              localStorage.removeItem("customerToken");
            }
          }
        }

        if (customerToken) {
          setToken(customerToken);

          // Fetch customer profile from API
          const response = await apiService.getCustomerProfile();
          if (response.success && response.data) {
            // Extract organizationId from JWT token
            const organizationId = getOrganizationIdFromToken(customerToken);
            const customerData = {
              ...response.data,
              organizationId: organizationId || ''
            };
            setCustomer(customerData);
            setIsLoading(false); // Set loading to false after successful customer auth
          } else {
            // Only clear auth for authentication-related errors (401, 403)
            // Don't logout for rate limiting (429), server errors (5xx), or network issues
            if (shouldLogoutOnError(response.statusCode)) {
              console.warn("Customer authentication failed, clearing tokens:", response.statusCode, response.message);
              localStorage.removeItem(`customerToken_${portalContext.organizationInfo.organizationId}`);
              clearAuth();
              setIsLoading(false); // Set loading to false after clearing auth
            } else {
              // For other errors (rate limiting, server errors), just log but don't logout
              console.warn("Error getting customer profile, but not logging out:", {
                statusCode: response.statusCode,
                message: response.message,
                errorType: getErrorType(response.statusCode)
              });
              setIsLoading(false); // Set loading to false even with errors
            }
          }
        } else {
          // No customer token - not authenticated
          setIsLoading(false);
        }
      } else if (!isCustomerPortalRoute) {
        // For admin/agent portal routes
        setIsPortalContextReady(true);

        // ALWAYS attempt refresh if both tokens exist to get fresh claims
        if (storedToken && storedRefreshToken) {
          console.log("Found tokens, attempting refresh to get fresh claims...");

          try {
            const refreshSuccess = await forceRefreshToken();
            if (refreshSuccess) {
              console.log("Token refresh successful, continuing to verify token and set user state");
              // Don't return here - let the code continue to verify the token and properly set user state
              // This prevents a race condition where state updates haven't propagated yet
            } else {
              console.log("Token refresh failed, falling back to stored token verification");
            }
          } catch (error) {
            console.warn("Token refresh error:", error);
            // Fall through to stored token verification
          }
        }

        // Re-read token from localStorage (refresh may have updated it)
        const currentToken = localStorage.getItem("authToken");

        if (currentToken) {
          // Check if token is expired before proceeding
          if (isTokenExpired(currentToken)) {
            console.warn("Token is expired after refresh attempt, clearing auth");
            clearAuth();
            setIsLoading(false);
            return;
          }

          setToken(currentToken);

          // Verify token and get user info
          console.log("Verifying token and fetching current user data...");
          const response = await apiService.getCurrentUser();
          if (response.success && response.data) {
            console.log("User data retrieved successfully, setting auth state");
            setUser(response.data);
            updateOrganizationData(response.data);
            // Start proactive token refresh for verified token
            refreshTokenManager.startProactiveRefresh(currentToken);
            setIsLoading(false);
          } else {
            // Only clear auth for authentication-related errors (401, 403)
            // Don't logout for rate limiting (429), server errors (5xx), or network issues
            if (shouldLogoutOnError(response.statusCode)) {
              console.warn("Authentication failed, clearing tokens:", response.statusCode, response.message);
              clearAuth();
              setIsLoading(false);
            } else {
              // For other errors (rate limiting, server errors), just log but don't logout
              console.warn("Error getting user info, but not logging out:", {
                statusCode: response.statusCode,
                message: response.message,
                errorType: getErrorType(response.statusCode)
              });
              setIsLoading(false);
            }
          }
        } else {
          // No tokens available - not authenticated
          console.log("No auth tokens found, user not authenticated");
          setIsLoading(false);
        }
      } else {
        // No tokens available - mark as ready
        setIsPortalContextReady(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      clearAuth();
      setIsLoading(false); // Always set loading to false even on error
    }
  }, [updateOrganizationData, clearAuth, forceRefreshToken]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const safeInitializeAuth = async () => {
      // Prevent double-invocation in StrictMode by checking if component is still mounted
      if (!isMounted || abortController.signal.aborted) {
        console.log('Auth initialization aborted - component unmounted or StrictMode cleanup');
        return;
      }

      await initializeAuth();
    };

    safeInitializeAuth();

    return () => {
      isMounted = false;
      abortController.abort();
      // Clear any pending refresh in the manager
      refreshTokenManager.clear();
      // Stop proactive refresh timer on unmount
      refreshTokenManager.stopProactiveRefresh();
    };
  }, [initializeAuth]);

  // Handle app resume (visibilitychange is reliable on mobile PWA, unlike focus)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Only check for admin/agent portal users (not customer portal)
      if (!user || !token || window.location.pathname.includes('/portal')) {
        return;
      }

      const authToken = localStorage.getItem("authToken");
      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (!authToken || !storedRefreshToken) return;

      if (isTokenExpired(authToken)) {
        console.log("Token expired on resume, attempting refresh...");
        const success = await forceRefreshToken();
        if (success) {
          const newToken = localStorage.getItem("authToken");
          if (newToken) {
            refreshTokenManager.startProactiveRefresh(newToken);
          }
        }
      } else if (isTokenNearExpiry(authToken, 30)) {
        // Token expires within 30 minutes — proactively refresh
        // (setTimeout-based timer may have been killed during app suspension)
        console.log("Token near expiry on resume, proactively refreshing...");
        const success = await forceRefreshToken();
        if (success) {
          const newToken = localStorage.getItem("authToken");
          if (newToken) {
            refreshTokenManager.startProactiveRefresh(newToken);
          }
        }
      } else {
        // Token still valid — restart the proactive timer
        // (the old setTimeout was killed when the app was suspended)
        refreshTokenManager.startProactiveRefresh(authToken);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, token, forceRefreshToken]);


  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.login(credentials);

      if (response.success && response.data) {
        const {
          token: authToken,
          refreshToken,
          user: userData,
        } = response.data;

        setToken(authToken);
        setUser(userData);
        updateOrganizationData(userData);

        localStorage.setItem("authToken", authToken);
        localStorage.setItem("refreshToken", refreshToken);

        // Start proactive token refresh
        refreshTokenManager.startProactiveRefresh(authToken);

        return true;
      }

      // The server's reason (wrong password, account locked, can't reach server) is what the login page shows
      console.error("Login failed:", response.message);
      throw new Error(getApiErrorMessage(response, "Sign in failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.register(userData);

      if (response.success && response.data) {
        const {
          token: authToken,
          refreshToken,
          user: userInfo,
        } = response.data;

        setToken(authToken);
        setUser(userInfo);
        updateOrganizationData(userInfo);

        localStorage.setItem("authToken", authToken);
        localStorage.setItem("refreshToken", refreshToken);

        // Start proactive token refresh
        refreshTokenManager.startProactiveRefresh(authToken);

        return true;
      } else {
        console.error("Registration failed:", response.message);
        return false;
      }
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Stop proactive token refresh
      refreshTokenManager.stopProactiveRefresh();

      // Staff sessions only: the logout endpoint rejects customer-portal tokens, whose sessions aren't stored
      // server-side (signing out just drops the token)
      const isCustomerSession = window.location.pathname.includes('/portal') && customer && !user;
      if (!isCustomerSession) {
        // Before the token is cleared - the push unsubscribe call needs it
        await pushNotificationService.unsubscribeForLogout();
        await apiService.logout(localStorage.getItem("refreshToken"));
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Determine context and clear appropriate tokens
      const isCustomerPortalRoute = window.location.pathname.includes('/portal');

      if (isCustomerPortalRoute && customer && !user) {
        // Customer portal logout - only clear customer tokens
        clearCustomerAuth();
      } else if (!isCustomerPortalRoute && user && !customer) {
        // Admin portal logout - only clear admin tokens
        clearAdminAuth();
      } else {
        // Ambiguous case or both tokens exist - clear everything for safety
        clearAuth();
      }

      // Clear TanStack Query cache to prevent stale data from persisting
      queryClient.clear();
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      if (!storedRefreshToken) {
        return false;
      }

      const response = await apiService.refreshToken(storedRefreshToken);

      if (response.success && response.data) {
        const {
          token: newToken,
          refreshToken: newRefreshToken,
          user: userData,
        } = response.data;

        setToken(newToken);
        setUser(userData);
        updateOrganizationData(userData);

        localStorage.setItem("authToken", newToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        return true;
      } else {
        clearAuth();
        return false;
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      clearAuth();
      return false;
    }
  };

  const switchOrganization = async (
    organizationId: string
  ): Promise<boolean> => {
    try {
      // Find the target organization to get its subdomain
      const targetOrg = availableOrganizations.find(org => org.id === organizationId);
      if (!targetOrg) {
        console.error("Target organization not found in available organizations");
        return false;
      }

      // Call API to switch organization and get new JWT token
      const response = await apiService.switchOrganization({ organizationId });

      if (response.success && response.data) {
        const {
          token: newToken,
          refreshToken: newRefreshToken,
          user: userData,
        } = response.data;

        // Update tokens
        setToken(newToken);
        localStorage.setItem("authToken", newToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Update user and organization data
        setUser(userData);
        updateOrganizationData(userData);

        // If we're on a helpdesk route, stay on the same page (unified view)
        // Just reload to refresh data with new organization context
        window.location.reload();

        return true;
      }

      return false;
    } catch (error) {
      console.error("Organization switch error:", error);
      return false;
    }
  };

  const requestMagicLink = async (
    request: MagicLinkRequest
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.requestMagicLink(request);

      if (response.success) {
        return true;
      } else {
        console.error("Magic link request failed:", response.message);
        return false;
      }
    } catch (error) {
      console.error("Magic link request error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Deliberately doesn't toggle isLoading: that flag means "auth is initializing", and the portal layout replaces its
  // children with a full-page spinner while it's set - which would unmount the verify page mid-request and lose the
  // result. The verify and auto-login pages show their own progress instead.
  const authenticateWithMagicLink = async (token: string, organizationId: string): Promise<boolean> => {
    const response = await apiService.authenticateWithMagicLink(token, organizationId);

    if (response.success && response.data) {
      const { token: authToken, customer: customerData } = response.data;

      setToken(authToken);
      setCustomer(customerData);

      // Store token with organization-specific key
      apiService.setCustomerTokenForOrganization(authToken, organizationId);
      
      // Clear legacy customerToken if it exists
      localStorage.removeItem("customerToken");

      return true;
    }

    // Callers tell the customer why (expired, already used, can't reach server) rather than a generic failure
    console.error("Magic link authentication failed:", response.message);
    throw new Error(getApiErrorMessage(response, "This magic link could not be verified. Please request a new one."));
  };


  const isAuthenticated = (!!user && !!token) || (!!customer && !!token);
  const isCustomerPortal = !!customer && !user;
  
  // For portal routes, we need both auth initialization AND portal context to be ready
  const isCustomerPortalRoute = window.location.pathname.includes('/portal');
  const effectiveIsLoading = isCustomerPortalRoute 
    ? (isLoading || !isPortalContextReady) 
    : isLoading;

  const refreshUserProfile = async (): Promise<void> => {
    try {
      if (user && token) {
        const response = await apiService.getCurrentUser();
        if (response.success && response.data) {
          setUser(response.data);
          updateOrganizationData(response.data);
        }
      }
    } catch (error) {
      console.error("Error refreshing user profile:", error);
    }
  };

  const value: AuthContextType = {
    user,
    customer,
    token,
    currentOrganization,
    availableOrganizations,
    isAuthenticated,
    isCustomerPortal,
    isLoading: effectiveIsLoading,
    login,
    register,
    requestMagicLink,
    authenticateWithMagicLink,
    logout,
    refreshToken,
    refreshUserProfile,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
